import { useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { losPipelineApi } from "../../api/losPipelineApi";
import {
  LENDMARQ_PIPELINE_BUCKETS,
  dealsForBucket,
} from "../../constants/lendmarqPipelineBuckets";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { cachedDataItemSet } from "../../redux/actions/cachedDataActions";
import { CACHED_DATA_REDUCER_TYPES } from "../../redux/reducers/cachedDataReducer";
import PipelineTableScreen from "./PipelineTableScreen";

// The "Pipeline" board — one screen, four buckets selected by the Pipeline nav
// dropdown (Full Pipeline / Active Pipeline / Closed / Dead-Inactive). Each
// bucket is a client-side slice (`dealsForBucket`) of the same all-loans
// payload.
//
// Data source is the cached full pipeline in `state.cachedData.loanPipeline`
// (CACHED_DATA_REDUCER_TYPES.LOAN_PIPELINE), populated from
// `losPipelineApi.getLendmarqPipeline` (currently /underwriting/
// underwriting_pipeline). The pipeline is fetched once and reused across every
// bucket + the correspondents screens + the header loan search; the cachedData
// reducer invalidates it (→ null) whenever a loan is created/updated, so the
// next fetch pulls fresh data.

const CACHE_KEY = CACHED_DATA_REDUCER_TYPES.LOAN_PIPELINE;

const LendmarqPipelineScreen = ({ bucketKey = "ACTIVE" }) => {
  const bucket =
    LENDMARQ_PIPELINE_BUCKETS[bucketKey] || LENDMARQ_PIPELINE_BUCKETS.ACTIVE;
  useDocumentTitle(bucket.label);

  const dispatch = useDispatch();
  // Read the cached pipeline through a ref so fetchData can serve it without
  // re-creating the callback (which would otherwise churn on every cache write).
  const cached = useSelector((s) => s.cachedData?.[CACHE_KEY]);
  const cachedRef = useRef(cached);
  cachedRef.current = cached;

  const fetchData = useCallback(
    ({ onSuccessFn, onFailFn, onErrorFn }) => {
      const onError = onFailFn || onErrorFn || (() => {});
      const slice = (deals) => dealsForBucket(deals, bucket.key);
      // Serve from the cachedData reducer when present (no network on bucket
      // switches); otherwise fetch the full pipeline once and store it there.
      if (Array.isArray(cachedRef.current)) {
        onSuccessFn?.(slice(cachedRef.current));
        return;
      }
      losPipelineApi
        .getLendmarqPipeline()
        .then((deals) => {
          const all = Array.isArray(deals) ? deals : [];
          dispatch(cachedDataItemSet(CACHE_KEY, all));
          onSuccessFn?.(slice(all));
        })
        .catch(onError);
    },
    [bucket.key, dispatch],
  );

  return (
    // `key` forces a remount when the bucket changes — the screen is the same
    // component at every Pipeline route, so without this React reuses the
    // instance and the table keeps showing the prior bucket's rows.
    <PipelineTableScreen
      key={bucket.slug}
      title={bucket.label}
      // Per-bucket namespace so each board keeps its own saved layouts.
      storageKeyPrefix={`lendmarq-pipeline-${bucket.slug}`}
      fetchData={fetchData}
      statusField={bucket.statusField}
      statusOptions={bucket.statuses}
    />
  );
};

export default LendmarqPipelineScreen;
